import { type ReactNode } from 'react';

import type { IndicatorsDisplay } from '../../../core/components/annotations/components/indicators/types.js';
import type { AnnotationsTooltipConfig } from '../../../core/components/annotations/components/tooltip/types/annotations-tooltip-props.js';
import type { AnnotationsActionsHandler } from '../../../core/components/annotations/types/annotations.js';
import type { XYChartData } from '../../../xy-chart/types/xy-chart.js';
import { XYChart, XYChartAnnotations } from '../../../xy-chart/XYChartRoot.js';

export const ANNOTATIONS_KEYS = {
  trackId: '__annotationsTrackId',
  trackLabel: '__annotationsTrackLabel',
  trackColor: '__annotationsTrackColor',
  trackHidden: '__annotationsTrackHidden',
  trackSymbol: '__annotationsTrackSymbol',
  trackIndicators: '__annotationsTrackIndicators',
  markerStart: '__annotationsMarkerStart',
  markerEnd: '__annotationsMarkerEnd',
  markerTitle: '__annotationsMarkerTitle',
  markerDescription: '__annotationsMarkerDescription',
  markerColor: '__annotationsMarkerColor',
  markerHidden: '__annotationsMarkerHidden',
  markerSymbol: '__annotationsMarkerSymbol',
  markerIndicators: '__annotationsMarkerIndicators',
  markerPriority: '__annotationsMarkerPriority',
} as const;

type AnnotationsKey = (typeof ANNOTATIONS_KEYS)[keyof typeof ANNOTATIONS_KEYS];

const getAnnotationField = <T,>(d: unknown, key: AnnotationsKey): T =>
  (d as Record<string, unknown>)[key] as T;

export type XYAnnotationsConfig = {
  showLabels?: boolean;
  visibleTracksLimit?: number;
  annotationsActions?: AnnotationsActionsHandler;
  tooltip?: AnnotationsTooltipConfig;
};

export const renderXYAnnotations = (
  data: XYChartData[],
  xAxisId: string,
  config: XYAnnotationsConfig,
) => (
  <XYChart.Annotations
    data={data}
    xAxisId={xAxisId}
    showLabels={config.showLabels}
    fixedTracks={config.visibleTracksLimit}
    annotationsActions={config.annotationsActions}
  >
    <XYChartAnnotations.Track
      trackIdAccessor={ANNOTATIONS_KEYS.trackId}
      labelAccessor={ANNOTATIONS_KEYS.trackLabel}
      colorAccessor={ANNOTATIONS_KEYS.trackColor}
      hidden={(d) =>
        getAnnotationField<boolean>(d, ANNOTATIONS_KEYS.trackHidden)
      }
      symbol={(d) =>
        getAnnotationField<ReactNode>(d, ANNOTATIONS_KEYS.trackSymbol)
      }
      indicatorsDisplay={(d) =>
        getAnnotationField<IndicatorsDisplay>(
          d,
          ANNOTATIONS_KEYS.trackIndicators,
        )
      }
    />
    <XYChartAnnotations.Marker
      startAccessor={ANNOTATIONS_KEYS.markerStart}
      endAccessor={ANNOTATIONS_KEYS.markerEnd}
      trackByAccessor={ANNOTATIONS_KEYS.trackId}
      titleAccessor={ANNOTATIONS_KEYS.markerTitle}
      descriptionAccessor={ANNOTATIONS_KEYS.markerDescription}
      colorAccessor={ANNOTATIONS_KEYS.markerColor}
      hidden={(d) =>
        getAnnotationField<boolean>(d, ANNOTATIONS_KEYS.markerHidden)
      }
      symbol={(d) =>
        getAnnotationField<ReactNode>(d, ANNOTATIONS_KEYS.markerSymbol)
      }
      indicatorsDisplay={(d) =>
        getAnnotationField<IndicatorsDisplay>(
          d,
          ANNOTATIONS_KEYS.markerIndicators,
        )
      }
      priority={(d) =>
        getAnnotationField<number>(d, ANNOTATIONS_KEYS.markerPriority)
      }
    />
    {config.tooltip?.template && (
      <XYChartAnnotations.Tooltip hidden={config.tooltip.hidden}>
        {config.tooltip.template}
      </XYChartAnnotations.Tooltip>
    )}
  </XYChart.Annotations>
);
